import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Plus, RefreshCw } from "lucide-react";
import { Empty, ErrorNotice, Loading } from "../../components/Feedback";
import { getErrorMessage } from "../../lib/supabase";
import { useSession } from "../../state/SessionContext";
import type { JsonRecord } from "../../types";
import { ExecutionDetailDialog, ExecutionFormDialog } from "./ExecutionDialogs";
import { ExecutionFilters, type ExecutionFiltersValue } from "./ExecutionFilters";
import { ExecutionTable } from "./ExecutionTable";
import { createExecution, deleteExecution, listExecution, loadExecutionCatalogs, updateExecution, validateExecution, type ConcreteCatalogs } from "./executionService";
import {
  executionSelectorNodes,
  executionTypes,
  type ExecutionTypeKey,
} from "./executionTypes";

const initialFilters: ExecutionFiltersValue = {
  start: "",
  end: "",
  frontId: "",
  subfrontId: "",
  search: "",
};
const pageSize = 25;

export function ExecutionPage() {
  const { context } = useSession();
  const [type, setType] = useState<ExecutionTypeKey>("concreto");
  const [filters, setFilters] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<JsonRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [fronts, setFronts] = useState<JsonRecord[]>([]);
  const [subfronts, setSubfronts] = useState<JsonRecord[]>([]);
  const [canInsert, setCanInsert] = useState(false);
  const [concreteCatalogs, setConcreteCatalogs] = useState<ConcreteCatalogs>({
    fronts: [], subfronts: [], mixtures: [], specifications: [], pressureUnits: [], areas: [], structures: [], steelSpecifications: [],
  });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [editing, setEditing] = useState<JsonRecord | null>(null);
  const [details, setDetails] = useState<JsonRecord | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState("");
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const selectorRef = useRef<HTMLDetailsElement>(null);

  const config = useMemo(
    () => executionTypes.find((item) => item.key === type)!,
    [type],
  );
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const selectedLabel = config.group ? `${config.group} › ${config.label}` : config.label;

  const load = useCallback(async () => {
    if (!context || !config.enabled) return;

    setLoading(true);
    setError("");
    try {
      const listFilters = type === "acero_estructural" ? {
        fecha_desde: applied.start || null,
        fecha_hasta: applied.end || null,
        frente_id: applied.frontId || null,
        subfrente_id: applied.subfrontId || null,
        buscar: applied.search.trim() || null,
        pagina: page,
        limite: pageSize,
      } : {
        fecha_desde: applied.start || null,
        fecha_hasta: applied.end || null,
        frente_ids: applied.frontId ? [applied.frontId] : [],
        subfrente_ids: applied.subfrontId ? [applied.subfrontId] : [],
        buscar: applied.search.trim() || null,
        pagina: page,
        limite: pageSize,
      };
      const [result, catalogs] = await Promise.all([listExecution(context, type, listFilters), loadExecutionCatalogs(context, type)]);
      const nextRows = type === "acero_estructural"
        ? result.rows.map((row) => {
            const specification = catalogs.steelSpecifications.find((item) =>
              String(item.id || item.especificacion_id || "") === String(row.especificacion_id || ""));
            const code = String(specification?.codigo || "").trim();
            return { ...row, especificacion: code || row.especificacion || "" };
          })
        : result.rows;
      setRows(nextRows);
      setTotal(result.total);
      setFronts(catalogs?.fronts ?? result.fronts);
      setSubfronts(catalogs?.subfronts ?? result.subfronts);
      setCanInsert(result.canInsert);
      if (catalogs) setConcreteCatalogs(catalogs);
    } catch (caught) {
      setRows([]);
      setTotal(0);
      setError(getErrorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, [context, type, config.enabled, applied, page]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  function changeType(next: ExecutionTypeKey) {
    setType(next);
    setFilters(initialFilters);
    setApplied(initialFilters);
    setPage(1);
    setRows([]);
    setTotal(0);
    setFronts([]);
    setSubfronts([]);
    setCanInsert(false);
    setEditing(null);
    setCreating(false);
    setDetails(null);
    setError("");
    setExpandedGroup(null);
    if (selectorRef.current) selectorRef.current.open = false;
  }

  function notify(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 3500);
  }

  async function save(values: JsonRecord) {
    if (!context) return;
    setBusy(true);
    setDialogError("");
    try {
      if (editing) await updateExecution(context, type, values);
      else await createExecution(context, type, values);
      setEditing(null);
      setCreating(false);
      notify(type === "acero_estructural" ? "Suministro guardado correctamente." : "Registro guardado correctamente.");
      await load();
    } catch (caught) {
      setDialogError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: JsonRecord) {
    if (
      !context
      || !row.id
      || !confirm("¿Eliminar este registro? Se conservará para auditoría mediante eliminación lógica.")
    ) return;

    try {
      await deleteExecution(context, type, String(row.id));
      notify(type === "acero_estructural" ? "Suministro eliminado correctamente." : "Registro eliminado correctamente.");
      await load();
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  async function validate(row: JsonRecord) {
    if (!context || !row.id) return;
    setBusy(true);
    setError("");
    try {
      await validateExecution(context, type, String(row.id));
      notify("Colado validado correctamente.");
      await load();
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return <div className="execution-page">
    <div className="page-heading">
      <div>
        <span className="eyebrow">CONTROL DE PRODUCCIÓN</span>
        <h1>Ejecución de obra</h1>
        <p>Consulta y gestiona la ejecución física del proyecto y contrato activos.</p>
      </div>
      <div className="heading-actions">
        {config.enabled && canInsert && <button
          className="button primary"
          onClick={() => {
            setDialogError("");
            setCreating(true);
          }}
        >
          <Plus size={17} /> {type === "acero_estructural" ? "Nuevo suministro" : "Nuevo registro"}
        </button>}
        <button
          className="button secondary"
          disabled={!config.enabled || loading}
          onClick={() => void load()}
        >
          <RefreshCw size={17} /> Actualizar
        </button>
      </div>
    </div>

    <section className="panel execution-selector" style={{ overflow: "visible" }}>
      <div style={{ padding: 16, maxWidth: 420 }}>
        <span style={{ display: "block", marginBottom: 7, color: "#334943", fontSize: 13, fontWeight: 700 }}>
          Material / trabajo
        </span>
        <details ref={selectorRef} style={{ position: "relative" }}>
          <summary
            style={{
              height: 42,
              padding: "0 12px",
              border: "1px solid #cbd7d3",
              borderRadius: 8,
              background: "white",
              color: "var(--ink)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              cursor: "pointer",
              listStyle: "none",
              fontWeight: 600,
            }}
          >
            <span>{selectedLabel}</span>
            <ChevronDown size={16} />
          </summary>
          <div style={{
            position: "absolute",
            zIndex: 50,
            top: "calc(100% + 5px)",
            left: 0,
            width: "100%",
            maxHeight: 360,
            overflowY: "auto",
            padding: 6,
            border: "1px solid var(--line)",
            borderRadius: 9,
            background: "white",
            boxShadow: "0 14px 32px #17312b24",
          }}>
            {executionSelectorNodes.map((node) => node.kind === "item"
              ? <button
                  key={node.item.key}
                  type="button"
                  onClick={() => changeType(node.item.key)}
                  style={{
                    width: "100%",
                    minHeight: 38,
                    padding: "8px 10px",
                    border: 0,
                    borderRadius: 6,
                    background: node.item.key === type ? "#edf4f1" : "transparent",
                    color: "var(--ink)",
                    textAlign: "left",
                    fontWeight: node.item.key === type ? 700 : 600,
                  }}
                >
                  {node.label}
                </button>
              : <div key={node.label}>
                  <button
                    type="button"
                    onClick={() => setExpandedGroup((current) => current === node.label ? null : node.label)}
                    style={{
                      width: "100%",
                      minHeight: 38,
                      padding: "8px 10px",
                      border: 0,
                      borderRadius: 6,
                      background: "transparent",
                      color: "var(--ink)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      textAlign: "left",
                      fontWeight: 700,
                    }}
                  >
                    {node.label}
                    {expandedGroup === node.label ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </button>
                  {expandedGroup === node.label && <div style={{ paddingLeft: 14 }}>
                    {node.items.map((item) => <button
                      key={item.key}
                      type="button"
                      onClick={() => changeType(item.key)}
                      style={{
                        width: "100%",
                        minHeight: 36,
                        padding: "7px 10px",
                        border: 0,
                        borderRadius: 6,
                        background: item.key === type ? "#edf4f1" : "transparent",
                        color: "var(--ink)",
                        textAlign: "left",
                        fontWeight: item.key === type ? 700 : 500,
                      }}
                    >
                      {item.label}
                    </button>)}
                  </div>}
                </div>)}
          </div>
        </details>
      </div>
    </section>

    {!config.enabled
      ? <section className="panel execution-pending">
          <Empty message="Este tipo de trabajo aún no tiene registros configurados." />
        </section>
      : <section className="panel execution-list">
          {type === "acero_estructural" && <div className="execution-list-heading"><strong>Listado de suministros</strong><span>{total} registros</span></div>}
          <ExecutionFilters
            value={filters}
            fronts={fronts}
            subfronts={subfronts}
            onChange={setFilters}
            onApply={() => {
              setPage(1);
              setApplied(filters);
            }}
          />
          {error && <div className="execution-error"><ErrorNotice message={error} /></div>}
          {loading
            ? <Loading label={`Cargando registros de ${config.label.toLowerCase()}…`} />
            : <ExecutionTable
                config={config}
                rows={rows}
                onView={setDetails}
                onEdit={(row) => {
                  setDialogError("");
                  setEditing(row);
                }}
                onDelete={(row) => void remove(row)}
                onValidate={(row) => void validate(row)}
              />}
          {!loading && !error && <div className="pagination execution-pagination">
            <span>{total} registros</span>
            <button
              className="button ghost small"
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
            >Anterior</button>
            <span>{page} / {pages}</span>
            <button
              className="button ghost small"
              disabled={page >= pages}
              onClick={() => setPage((value) => value + 1)}
            >Siguiente</button>
          </div>}
        </section>}

    {(creating || editing) && <ExecutionFormDialog
      key={`${type}-${editing?.id || "new"}`}
      config={config}
      record={editing || undefined}
      fronts={fronts}
      subfronts={subfronts}
      busy={busy}
      error={dialogError}
      context={context!}
      concreteCatalogs={concreteCatalogs}
      onClose={() => {
        setCreating(false);
        setEditing(null);
      }}
      onSave={(values) => void save(values)}
    />}

    {details && <ExecutionDetailDialog
      config={config}
      record={details}
      onClose={() => setDetails(null)}
    />}

    {toast && <div className="quality-toast">{toast}</div>}
  </div>;
}
